import { useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { PlusCircle } from "lucide-react";
import Items from "./Items";
import { useScheduleContext } from "../../../../hooks/context/useScheduleContext";
import { useMenuContext } from "../../../../hooks/context/useMenuContext";
import { useMoreModal } from "./MoreModal/useMoreModal";
import { usePermissions } from "../../../../hooks/usePermissions";
import { useFittingItemsCount } from "../../../../hooks/useFittingItemsCount";
import { unassignedShiftsResource } from "../../../../helpers/data/unassignedShiftsResource";
import { cn } from "../../../../../../../lib/utils";

const MORE_BUTTON_HEIGHT = 17;

function Content({ day, dayData }: any) {
    const { t } = useTranslation();
    const { openMenu } = useMenuContext();
    const { unassignedEvents, openShifts, type } = useScheduleContext();
    const { open: openMoreModal, modal: moreModal } = useMoreModal();
    const {
        canCreate: canCreateEvent,
        canApprove: canApproveEvent,
        canCreateUnassignedShift
    } = usePermissions();
    const canAdd = canCreateEvent || canCreateUnassignedShift || canApproveEvent;

    const containerRef = useRef<HTMLDivElement>(null);

    const { allEvents, total } = useMemo(() => {
        let allEvents = [...(dayData?.data ?? [])];
        const dailyUnassignedEvents = unassignedEvents?.filter((event: any) =>
            moment(event.date).isSame(moment(day), "date")
        ) ?? [];

        if (type === "team") {
            allEvents = [...allEvents, ...dailyUnassignedEvents];
        }

        if (type === "home") {
            const dailyOpenShifts = openShifts?.filter((event: any) =>
                moment(event.data?.schedule?.startDate || event.data?.startDate).isSame(moment(day), "date")
            ) ?? [];
            allEvents = [...allEvents, ...dailyOpenShifts];
        }

        const loadedCalendarEvents = (dayData?.data ?? []).filter((item: any) => item.type !== "daily-info").length;
        const notLoaded = Math.max(0, (dayData?.total ?? 0) - loadedCalendarEvents);

        return { allEvents, total: allEvents.length + notLoaded };
    }, [dayData?.data, dayData?.total, unassignedEvents, openShifts, day, type]);

    const visibleCount = useFittingItemsCount(
        containerRef,
        allEvents.length,
        MORE_BUTTON_HEIGHT,
        total > allEvents.length
    );
    const hiddenEvents = useMemo(() => allEvents.slice(visibleCount), [allEvents, visibleCount]);
    // Only the shifts shown in the cell are excluded from the modal - loaded ones that didn't fit must still appear there
    const visibleShiftIds = useMemo(() => {
        const loadedIds = new Set((dayData?.data ?? []).map((item: any) => item.id));
        return allEvents.slice(0, visibleCount)
            .filter((item: any) => loadedIds.has(item.id))
            .map((item: any) => item.id);
    }, [allEvents, visibleCount, dayData?.data]);
    const hiddenCount = total - visibleCount;

    const onMenuOpen = useCallback((e: any) => {
        e.preventDefault();
        e.stopPropagation();
        openMenu({
            id: "cell-menu-without-events",
            x: e.clientX,
            y: e.clientY,
            selected: day.format("YYYY-MM-DD")
        });
    }, [openMenu, day]);

    return (
        <div
            ref={containerRef}
            key={moment(day).format("YYYY-MM-DD") + dayData?.data?.length}
            className="tw-flex tw-flex-col tw-overflow-hidden tw-flex-1 tw-min-h-0 tw-relative"
        >
            {total === 0 && (
                <div
                    className={cn(
                        "tw-group tw-flex-1 tw-min-h-0 tw-w-full tw-flex tw-items-center tw-justify-center tw-rounded-sm tw-overflow-hidden tw-transition-colors",
                        canAdd && "hover:tw-bg-primary/10"
                    )}
                >
                    {canAdd && (
                        <PlusCircle
                            className="tw-text-primary tw-cursor-pointer tw-shrink tw-max-h-full tw-opacity-0 tw-pointer-events-none group-hover:tw-opacity-100 group-hover:tw-pointer-events-auto tw-transition-opacity"
                            size={30}
                            onClick={onMenuOpen}
                        />
                    )}
                </div>
            )}
            {allEvents.map((item: any, idx: number) => {
                const type = item.resourceId === unassignedShiftsResource.id ? "unassigned" : item.type;
                const isHidden = idx >= visibleCount;

                return (
                    <div
                        key={idx}
                        data-fitting-item
                        aria-hidden={isHidden}
                        className={cn(
                            "tw-truncate tw-text-sm tw-leading-6 tw-shrink-0",
                            isHidden && "tw-invisible tw-absolute tw-inset-x-0 tw-top-0 tw-pointer-events-none"
                        )}
                    >
                        <Items event={item} type={type} day={day} />
                    </div>
                );
            })}

            {hiddenCount > 0 && (
                <div
                    className="tw-mt-auto tw-shrink-0 tw-text-xs tw-text-right tw-text-primary/60 tw-cursor-pointer tw-pr-2"
                    style={{ height: MORE_BUTTON_HEIGHT, lineHeight: `${MORE_BUTTON_HEIGHT}px` }}
                    onClick={() => openMoreModal({
                        date: day,
                        exclude: visibleShiftIds,
                        hiddenEvents
                    })}
                >
                    +{hiddenCount} {t("more")}
                </div>
            )}
            {moreModal}
        </div>
    );
}

export default Content;
